import { useCallback, useState } from "react";

type PendingLeave = { onLeave: () => void; onStay: (() => void) | null };

/**
 * Asks before something would throw away unsaved work: `guard(onLeave, onStay)` runs `onLeave` at once when
 * nothing is unsaved, else holds it for the dialog. Leaving discards the work first; staying runs `onStay`.
 */
export const useLeaveGuard = (isDirty: boolean, discard: () => void) => {
	const [pending, setPending] = useState<PendingLeave | null>(null);

	const guard = useCallback(
		(onLeave: () => void, onStay?: () => void) => {
			if (!isDirty) onLeave();
			else setPending({ onLeave, onStay: onStay === undefined ? null : onStay });
		},
		[isDirty],
	);

	const leave = useCallback(() => {
		if (pending === null) return;
		setPending(null);
		discard();
		pending.onLeave();
	}, [pending, discard]);

	const stay = useCallback(() => {
		if (pending !== null && pending.onStay !== null) pending.onStay();
		setPending(null);
	}, [pending]);

	return { guard, isAsking: pending !== null, leave, stay };
};
